import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useJournalData } from "../context/JournalContext";
import { useTradeStats } from "../hooks/useTradeStats";
import { SECTIONS } from "../constants/sections";
import {
  SidebarContainer,
  SidebarHeader,
  SidebarBrand,
  BrandLeft,
  BrandLogo,
  BrandText,
  BrandName,
  BrandSub,
  AccountBadge,
  SidebarSummary,
  SummaryRow,
  SummaryValue,
  SidebarSectionLabel,
  Nav,
  NavButton,
  NavIcon,
  SidebarFooter
} from "./SidebarStyles";

const ICONS = {
  "economy": "🌐",
  "fundamentals": "🏛️",
  "market-internals": "⚙️",
  "market-overview": "🧭",
  "seasonality": "♻️",
  "intermarket": "🔀",
  "chart-analysis": "📐",
  "upcoming-events": "🗓️",
  "metrics": "🧮",
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, rows } = useJournalData();
  const stats = useTradeStats(rows || [], userProfile?.balance || 10000, userProfile);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val);
  };

  const formatNumber = (val, decimals = 2) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(val);
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <SidebarBrand>
          <BrandLeft>
            <BrandLogo>TT</BrandLogo>
            <BrandText>
              <BrandName>Trade Terminal</BrandName>
              <BrandSub>Trading Workspace</BrandSub>
              <AccountBadge>{userProfile?.name || 'Active'}</AccountBadge>
            </BrandText>
          </BrandLeft>
        </SidebarBrand>

        <SidebarSummary>
          <SummaryRow>
            <span>Balance</span>
            <SummaryValue positive>{formatCurrency(stats?.balance ?? userProfile?.equity ?? userProfile?.balance ?? 0)}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <span>Max Drawdown</span>
            <SummaryValue negative={(stats?.maxDrawdown ?? userProfile?.max_drawdown ?? 0) > 10}>
              {formatNumber(stats?.maxDrawdown ?? userProfile?.max_drawdown ?? 0)}%
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <span>Expectancy</span>
            <SummaryValue positive={(stats?.expectancyR || 0) > 0} negative={(stats?.expectancyR || 0) < 0}>
              {(stats?.expectancyR || 0) > 0 ? `+${formatNumber(stats.expectancyR, 2)} R` : `${formatNumber(stats?.expectancyR || 0, 2)} R`}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <span>Profit Factor</span>
            <SummaryValue positive={(stats?.profitFactor ?? userProfile?.profit_factor ?? 0) > 1.5} negative={(stats?.profitFactor ?? userProfile?.profit_factor ?? 0) < 1}>
              {(stats?.profitFactor === Infinity || stats?.profitFactor === 999 || userProfile?.profit_factor === 999) ? '∞' : formatNumber(stats?.profitFactor ?? userProfile?.profit_factor ?? 0)}
            </SummaryValue>
          </SummaryRow>
        </SidebarSummary>
      </SidebarHeader>

      <div>
        <SidebarSectionLabel>Navigation</SidebarSectionLabel>
        <Nav>
          {Object.entries(SECTIONS).map(([key, cfg]) => {
            const isActive = location.pathname.startsWith(cfg.path);
            return (
              <NavButton
                key={key}
                onClick={() => navigate(cfg.path)}
                active={isActive}
              >
                <NavIcon>{ICONS[key] || "📄"}</NavIcon>
                <span>{cfg.label === "Metrics" ? "Account metrix" : (cfg.label === "Economic Calendar" ? "Economic board" : cfg.label)}</span>
              </NavButton>
            );
          })}
        </Nav>
      </div>

      <SidebarFooter>
        <span><strong>Version:</strong> v1.0.0 • Beta </span>
      </SidebarFooter>
    </SidebarContainer>
  );
}
